// Catalog.ID Client — Delegates List Pane
// ============================================================================
// Lists delegates for the active organization. Title row: invite delegate.

import { createListPaneType, createElement, renderPaneActions } from '@catalog-shell';
import type { ListPaneConfig, PaneActionDescriptor, PaneTypeRenderContext } from '@catalog-shell';
import { DataSource } from '@catalog-shell/components';
import type { ColumnDef, FetchResult } from '@catalog-shell/components';
import * as organizationApi from '../api/organization';
import { formatApiError } from '../api/client';
import type { DelegateSummary } from '../api/types';
import { identity } from '../state/identity';
import { getDashboardPort } from '../state/dashboard-context';
import { session } from '../state/session';
import { keys } from '../state/keys';
import { getKeyBagEntry } from '../crypto/keybag';
import * as aes from '../crypto/aes';
import * as invitationApi from '../api/invitation';
import { fetchRegistrationConfig } from '../auth/payment-flow';
import {
    relockBits, relockPlanFor, INVITATION_LOCK_GRACE_MS,
    findFreeParkingSlot, parkingConditions,
    type RelockOutcome, type RelockLeg,
} from '../auth/bits-release';
import {
    loadUnusedCodes, markCodeIssued, type StoredVoucherCode,
} from '../state/voucher-code-store';
import { bitcashWorker } from '../state/bitcash-context';
import { unitsToBit, unitsToWholeBit } from '../api/bitcash-units';
import { lockKeyFor, signLockRelease, displayWalletKey } from '../crypto/bitcash';
import {
    buildInvitationDocument, canonicalInvitationJson, signInvitation,
    mintInvitationCode, deriveInvitationSecrets, formatInvitationCode,
} from '../auth/invitation-document';
import {
    deriveVoucherSecrets, normalizeVoucherCode, formatVoucherCode,
} from '../auth/voucher-code';
import { activationUrlFor } from '../auth/delegate-activation-ref';
import { buildInvitationCardSheet, type InvitationCardData } from '../components/booklet/invitation-card';
import { printPdf, downloadBlob } from '../components/pdf-loader';
import { PANE_IDS } from '../shell-config';

const STATUS_COLORS: Record<string, string> = {
    active: 'rgba(80,200,80,0.3)',
    partial: 'rgba(100,149,237,0.3)',
    frozen: 'rgba(220,150,50,0.3)',
    terminated: 'rgba(220,50,50,0.3)',
};

const STATUS_LABELS: Record<string, string> = {
    active: 'Active',
    partial: 'Pending Onboarding',
    frozen: 'Frozen',
    terminated: 'Terminated',
};

const columns: ColumnDef<DelegateSummary>[] = [
    {
        key: 'cid',
        header: 'CID',
        width: 140,
        getValue: (item) => item.cid,
        type: { kind: 'text' },
    },
    {
        key: 'roles',
        header: 'Roles',
        width: 160,
        getValue: (item) => item.roles.join(', '),
        type: { kind: 'text' },
    },
    {
        key: 'status',
        header: 'Status',
        width: 120,
        getValue: (item) => item.status,
        type: {
            kind: 'badge',
            backgroundColor: (v) => STATUS_COLORS[v as string] ?? '#555',
            converter: (v) => STATUS_LABELS[v as string] ?? String(v),
        },
    },
    {
        key: 'creationTime',
        header: 'Created',
        width: 100,
        sortable: true,
        getValue: (item) => item.creationTime,
        type: {
            kind: 'text',
            converter: (v) => {
                try { return new Date(v as string).toLocaleDateString(); }
                catch { return String(v); }
            },
        },
        responsiveHide: { minContainerWidth: 600, priority: 1 },
    },
];

function createDelegatesDataSource(): DataSource<DelegateSummary> {
    return new DataSource<DelegateSummary>({
        fetch: async (params): Promise<FetchResult<DelegateSummary>> => {
            const orgCid = identity.getOrganizationCid();
            if (!orgCid) return { items: [], totalCount: 0 };
            const delegates = await organizationApi.listDelegates(orgCid);
            const sliced = delegates.slice(params.offset, params.offset + params.limit);
            return { items: sliced, totalCount: delegates.length };
        },
        keyFn: (item) => item.cid,
        pageSize: 100,
    });
}

const config: ListPaneConfig<DelegateSummary> = {
    typeId: PANE_IDS.delegatesList,
    defaultTitle: 'Delegates',
    createDataSource: () => createDelegatesDataSource(),
    columns,
    keyFn: (item) => item.cid,
    initialMode: 'table',
    enabledModes: ['table'],
    selectionMode: 'none',
    onCreated: ({ dataSource }, ctx) => {
        ctx.disposables.add(ctx.shell.on('data-changed', (data) => {
            if ((data as { type?: string })?.type === 'delegates') {
                dataSource.refresh();
            }
        }));
    },
    onItemSelect: (item, ctx) => {
        ctx.surface?.openPane({
            typeId: PANE_IDS.delegateDetail,
            title: item.cid,
            context: { delegateCid: item.cid, delegate: item },
        }, { originPaneId: ctx.instance.instanceId });
    },
    tabBarActions: (ctx, dataSource) => renderPaneActions(
        planDelegatesListActions(identity.getRoles().includes('owner'), {
            newInvitation: () => void handleNewInvitation(ctx.shell, dataSource),
            invitations: () => void handleInvitations(ctx.shell, dataSource),
        })),
    ariaLabel: 'Delegates list',
};

/**
 * Pure: the list's own verbs, for its title row (shell USAGE.md "Where a button
 * belongs"). Signed, voucher-backed invitations (registration-restructure
 * §13.5) are an owner's to create; the invitation list is open to every role.
 * The owner-only verb is DISABLED with its reason rather than omitted — an
 * admin looking at an empty delegate list should be able to see where
 * invitations come from, even though they cannot issue one.
 */
export function planDelegatesListActions(
    isOwner: boolean,
    exec: { newInvitation: () => void; invitations: () => void },
): PaneActionDescriptor[] {
    return [
        {
            key: 'new-invitation',
            label: '+ New Invitation',
            variant: 'primary',
            disabled: !isOwner,
            title: isOwner ? undefined : 'Only an owner can create invitations',
            execute: () => exec.newInvitation(),
        },
        {
            key: 'invitations',
            label: 'Invitations',
            execute: () => exec.invitations(),
        },
    ];
}

const ROLES = ['owner', 'admin', 'treasurer', 'viewer'];
/** Client-side default; the server clamps to `Invitation:ExpiryDays` (30). */
const INVITATION_DAYS = 30;

function para(text: string, className = 'cid-wizard-text'): HTMLElement {
    return createElement('p', { className, textContent: text });
}

function textInput(placeholder: string, type = 'text'): HTMLInputElement {
    const input = createElement('input', { className: 'cid-auth-input' }) as HTMLInputElement;
    input.type = type;
    input.placeholder = placeholder;
    input.autocomplete = 'off';
    return input;
}

/**
 * Create a signed invitation (registration-restructure §13.5, M15; §14.2, M18). The
 * document is built and signed HERE with the owner's main_signing_key; the
 * node verifies it, reserves the chosen delegate voucher and returns the
 * card's id + secret. The printed card carries the node's public URL; the
 * "open on this machine" link carries `?dp=` for the helper origin (§11).
 */
async function handleNewInvitation(shell: PaneTypeRenderContext['shell'], dataSource: DataSource<DelegateSummary>): Promise<void> {
    const orgCid = identity.getOrganizationCid();
    if (!orgCid) { shell.toast('No organization context', 'error'); return; }
    if (!identity.getRoles().includes('owner')) { shell.toast('Only an owner can create invitations', 'warning'); return; }
    const inviterCid = session.getInfo()?.cid;
    const bag = keys.getKeyBag();
    const signingEntry = bag ? getKeyBagEntry(bag, 'main_signing_key') : null;
    if (!inviterCid || !signingEntry) { shell.toast('Unlock your keys to sign an invitation', 'error'); return; }
    // Payment mode and the node's standalone site (fail closed: payment required,
    // no site → the card links to this dashboard's own origin).
    let cfg: { paymentRequired: boolean; siteBaseUrl: string | null } = { paymentRequired: true, siteBaseUrl: null };
    try {
        const fetched = await fetchRegistrationConfig();
        cfg = { paymentRequired: fetched.paymentRequired !== false, siteBaseUrl: fetched.siteBaseUrl };
    } catch { /* keep the fail-closed defaults */ }

    const content = createElement('div', { className: 'cid-wizard-step' });
    content.appendChild(para('Roles for the new delegate:'));
    const selectedRoles = new Set<string>(['viewer']);
    for (const role of ROLES) {
        const row = createElement('div', { className: 'cid-auth-checkbox-row' });
        const cb = document.createElement('input');
        cb.type = 'checkbox';
        cb.id = `invite-role-${role}`;
        cb.checked = selectedRoles.has(role);
        cb.addEventListener('change', () => {
            if (cb.checked) selectedRoles.add(role);
            else selectedRoles.delete(role);
        });
        const label = createElement('label', { textContent: role.charAt(0).toUpperCase() + role.slice(1) });
        label.setAttribute('for', cb.id);
        row.appendChild(cb);
        row.appendChild(label);
        content.appendChild(row);
    }

    content.appendChild(para('Optionally fix the email address and phone number the invitee must verify. '
        + 'They are printed on the card in plain text and the invitee types them; the verification will '
        + 'accept no other value. The node stores only a salted digest — it never learns the address.'));
    const emailInput = textInput('Email address (optional)', 'email');
    const phoneInput = textInput('Phone number in international format, e.g. +31612345678 (optional)', 'tel');
    content.appendChild(emailInput);
    content.appendChild(phoneInput);

    const voucherArea = createElement('div');
    content.appendChild(voucherArea);
    const paymentRequired = cfg.paymentRequired;
    let voucherSelect: HTMLSelectElement | null = null;
    let voucherInput: HTMLInputElement | null = null;
    /** The device-kept codes, so the printed card can state the bits the node
     *  never learned (D19 — this browser is the only place that figure lives). */
    let storedCodes: StoredVoucherCode[] = [];

    const submitBtn = createElement('button', { className: 'cs-btn cs-btn--yes', textContent: 'Create Invitation' });
    content.appendChild(submitBtn);
    const modal = shell.showModal(content, { size: 'normal' });

    void (async () => {
        if (!paymentRequired) {
            voucherArea.appendChild(para('Delegates are free on this server — no voucher needed.', 'cid-pane-note'));
            return;
        }
        // THE CODES COME FROM THIS DEVICE, not the ledger (D19). The node
        // stores a hash of a lookup id and can hand back no code at all — the
        // `code` field the ledger used to carry is gone, and the only copies
        // are this browser's store and the paper the Vouchers pane printed.
        const stored = await loadUnusedCodes(session.getInfo()?.cid);
        storedCodes = stored;
        voucherArea.appendChild(para('Delegate voucher — one is consumed when the invitee activates. '
            + 'Its bits move onto the invitation, and come back to you if it is never used:'));
        voucherSelect = document.createElement('select');
        voucherSelect.className = 'cid-auth-input';
        const blank = document.createElement('option');
        blank.value = '';
        blank.textContent = stored.length > 0
            ? 'Choose a code kept on this device'
            : 'No codes kept on this device — buy some in Vouchers, or paste one from paper';
        voucherSelect.appendChild(blank);
        for (const v of stored) {
            const opt = document.createElement('option');
            opt.value = v.code;
            const bits = v.bits ? `${v.bits} bits` : 'bits unknown';
            opt.textContent = `${formatVoucherCode(v.code)} — ${bits}`
                + (v.expiresAt ? ` (valid until ${new Date(v.expiresAt).toLocaleDateString()})` : '');
            voucherSelect.appendChild(opt);
        }
        voucherArea.appendChild(voucherSelect);
        voucherInput = textInput('Or paste a voucher code');
        voucherArea.appendChild(voucherInput);
    })();

    submitBtn.addEventListener('click', async () => {
        if (selectedRoles.size === 0) { shell.toast('Select at least one role', 'warning'); return; }
        const voucherCode = paymentRequired ? (voucherInput?.value.trim() || voucherSelect?.value || '') : '';
        if (paymentRequired && !voucherCode) { shell.toast('Choose or paste a delegate voucher', 'warning'); return; }
        const fixedEmail = emailInput.value.trim() || undefined;
        const fixedPhone = phoneInput.value.trim() || undefined;
        const roles = ROLES.filter(r => selectedRoles.has(r));

        submitBtn.setAttribute('disabled', '');
        submitBtn.textContent = 'Signing and registering...';
        try {
            // The code is minted HERE and never sent (§14.2). One scrypt turns
            // it into the lookup id the node is given, and the salt that hashes
            // the fixed values — the salt goes into no request and no column, so
            // nobody holding the node's database can scan those hashes for a
            // known address.
            const code = mintInvitationCode();
            submitBtn.textContent = 'Deriving the invitation code...';
            const {
                lookupIdHex, saltHex, publicKey, publicKeyHex,
            } = await deriveInvitationSecrets(code);

            submitBtn.textContent = 'Signing and registering...';
            const now = new Date();
            const expiresAt = new Date(now.getTime() + INVITATION_DAYS * 86_400_000);
            // v3 (registration-wallets §4.1, D16): the document carries the
            // voucher's LOOKUP ID, never its code — `documentjson` is stored
            // verbatim, so a plaintext code here is a code on the server.
            // Waived node (no vouchers at all): the key is OMITTED from the
            // signed document, never empty — presence is what the server reads.
            const voucherSecrets = paymentRequired
                ? await deriveVoucherSecrets(voucherCode) : null;
            const doc = buildInvitationDocument({
                orgCid, inviterCid, roles, valueSalt: saltHex,
                ...(voucherSecrets ? { voucherLookupId: voucherSecrets.lookupIdHex } : {}),
                fixedEmail, fixedPhone, now, expiresAt,
            });
            const documentJson = canonicalInvitationJson(doc);
            const signature = signInvitation(doc, aes.fromBase64(signingEntry.privateKey));

            // ── CID first, Worker second (§4.4) ──────────────────────────────
            // A failure at the node moves nothing. A failure at the Worker
            // leaves a VALID invitation whose bits are still on the voucher
            // lock — recoverable by repeating the relock, which is idempotent.
            // The other order would strand bits under a key that names no
            // invitation.
            const resp = await invitationApi.registerInvitation(orgCid, { documentJson, signature, lookupId: lookupIdHex });

            let relock: RelockOutcome = { status: 'relocked' };
            let legs: RelockLeg[] = [];
            let lockedBits: number | undefined;
            if (voucherSecrets) {
                submitBtn.textContent = 'Moving the bits…';
                // What this voucher is really worth. The device-kept figure is
                // what THIS organization paid, and it is absent altogether for a
                // code pasted off paper — delegate stock is universal (D18), so
                // the code may have been bought by anyone. The Worker's balance
                // is the only truth; a lock that cannot be read leaves the card
                // without a bits line rather than printing a guess.
                try {
                    const info = await bitcashWorker().lockInfo(lockKeyFor(voucherSecrets.publicKey, 0));
                    if (info.exists && !info.closed) lockedBits = unitsToWholeBit(info.balance) ?? undefined;
                } catch { /* no figure — the card prints without the bits line */ }
                legs = relockPlanFor({
                    voucher: {
                        lockKey: lockKeyFor(voucherSecrets.publicKey, 0),
                        publicKeyHex: voucherSecrets.publicKeyHex,
                    },
                    invitation: { publicKey, publicKeyHex },
                    // A DELEGATE voucher carries one lock: slot 1 is the
                    // organization founder's alone (§4.1).
                    registrationType: 'delegate',
                    expiresAt: resp.expiresAt,
                });
                relock = await relockBits(legs, voucherSecrets.seed, { worker: bitcashWorker() });
                // The code is spent either way — the node reserved its row — so
                // the store records it now, keeping BOTH codes: the voucher code
                // is what reclaims the bits if this invitation is never used.
                await markCodeIssued(session.getInfo()?.cid, voucherCode, code);
            }

            renderInvitationResult(content, modal, dataSource, {
                orgCid, roles, fixedEmail, fixedPhone, code, resp,
                siteBaseUrl: cfg.siteBaseUrl,
                // The Worker first, the device's own record as a fallback.
                bits: lockedBits ?? storedBitsFor(voucherCode, storedCodes),
                relock, legs,
                voucherSeed: voucherSecrets?.seed ?? null,
            });
        } catch (err) {
            submitBtn.removeAttribute('disabled');
            submitBtn.textContent = 'Create Invitation';
            shell.toast(formatApiError(err, 'Failed to create invitation'), 'error');
        }
    });
}

/** The bits a device-kept code carries, or undefined. The node knows none of
 *  this — it is what makes the printed card able to say what it is worth. */
function storedBitsFor(code: string, stored: readonly StoredVoucherCode[]): number | undefined {
    const normalised = normalizeVoucherCode(code);
    return stored.find(c => c.code === normalised)?.bits;
}

function renderInvitationResult(
    content: HTMLElement,
    modal: { close(): void },
    dataSource: DataSource<DelegateSummary>,
    args: {
        orgCid: string; roles: string[]; fixedEmail?: string; fixedPhone?: string;
        code: string; resp: invitationApi.RegisterInvitationResponse; siteBaseUrl: string | null;
        bits?: number;
        relock: RelockOutcome;
        legs: RelockLeg[];
        voucherSeed: Uint8Array | null;
    },
): void {
    const { orgCid, roles, fixedEmail, fixedPhone, code, resp, siteBaseUrl, relock } = args;
    const display = formatInvitationCode(code);
    // The node's standalone site when it announces one (production), else this
    // dashboard's own origin with `dp` (dev) — either way the link opens the app.
    const site = { siteBaseUrl, origin: window.location.origin, dashboardPort: getDashboardPort() };
    // The organization rides beside the code (docs/cid-governance.md M7a): the
    // invitation lives only at the organization's home server, and whichever
    // node the invitee's client starts from sends them there first.
    const url = activationUrlFor({ code, orgCid }, site);
    // The same address without the fragment: what the card prints as its
    // typeable line, beside the code in 12 pt.
    const siteUrl = activationUrlFor({}, site);
    // The bits line and its own deadline (D20, D28). Printed only when the
    // relock actually landed — a card promising bits that are still on the
    // voucher would be a lie in ink.
    const carriesBits = relock.status === 'relocked' && args.bits !== undefined;
    const card: InvitationCardData = {
        code: display, orgCid, roles, email: fixedEmail, phone: fixedPhone,
        expiresAt: new Date(resp.expiresAt), url, siteUrl,
        ...(carriesBits ? {
            bits: args.bits,
            bitsExpireAt: new Date(Date.parse(resp.expiresAt) + INVITATION_LOCK_GRACE_MS),
        } : {}),
    };

    content.innerHTML = '';
    content.appendChild(createElement('h3', { textContent: `Invitation #${resp.invitationId} created` }));
    content.appendChild(para('This code is shown ONLY NOW — the node never received it and cannot show it '
        + 'again. Print the card, download the PNG, or read the code out; a lost code means revoking this '
        + 'invitation and creating a new one.'));

    // The code, big and selectable: it is what the invitee types, and what gets
    // read over the telephone when the card is somewhere else.
    const codeInput = createElement('input', { className: 'cid-auth-input cid-credential-value' }) as HTMLInputElement;
    codeInput.type = 'text';
    codeInput.value = display;
    codeInput.readOnly = true;
    codeInput.addEventListener('click', () => codeInput.select());
    content.appendChild(codeInput);

    const linkInput = createElement('input', { className: 'cid-auth-input' }) as HTMLInputElement;
    linkInput.type = 'text';
    linkInput.value = url;
    linkInput.readOnly = true;
    linkInput.addEventListener('click', () => linkInput.select());
    content.appendChild(linkInput);

    const actions = createElement('div', { className: 'cs-pane-toolbar' });
    const printBtn = createElement('button', { className: 'cs-btn cs-btn--yes cs-btn--small', textContent: 'Print card' });
    printBtn.addEventListener('click', () => {
        // Synchronous from the click: printPdf opens its window immediately (popup rules).
        void printPdf(buildInvitationCardSheet([card]));
    });
    actions.appendChild(printBtn);
    const pngBtn = createElement('button', { className: 'cs-btn cs-btn--default cs-btn--small', textContent: 'Download PNG' });
    pngBtn.addEventListener('click', async () => {
        pngBtn.setAttribute('disabled', '');
        try {
            const { exportInvitationCardPng } = await import('../components/booklet/invitation-card-png');
            downloadBlob(await exportInvitationCardPng(card), `invitation-${resp.invitationId}.png`);
        } finally {
            pngBtn.removeAttribute('disabled');
        }
    });
    actions.appendChild(pngBtn);
    const copyCodeBtn = createElement('button', { className: 'cs-btn cs-btn--default cs-btn--small', textContent: 'Copy code' });
    copyCodeBtn.addEventListener('click', async () => {
        await navigator.clipboard.writeText(display);
        copyCodeBtn.textContent = 'Copied!';
        setTimeout(() => { copyCodeBtn.textContent = 'Copy code'; }, 2000);
    });
    actions.appendChild(copyCodeBtn);
    const copyBtn = createElement('button', { className: 'cs-btn cs-btn--default cs-btn--small', textContent: 'Copy link' });
    copyBtn.addEventListener('click', async () => {
        await navigator.clipboard.writeText(url);
        copyBtn.textContent = 'Copied!';
        setTimeout(() => { copyBtn.textContent = 'Copy link'; }, 2000);
    });
    actions.appendChild(copyBtn);
    content.appendChild(actions);

    // The Worker leg, when it did not finish. The invitation is already valid —
    // this is about the money, and repeating it is safe (§4.4).
    if (relock.status === 'partial' && args.voucherSeed) {
        const note = createElement('p', { className: 'cid-pane-note', textContent: relock.message });
        content.appendChild(note);
        let pending = relock.pending;
        const seed = args.voucherSeed;
        const retryBtn = createElement('button', {
            className: 'cs-btn cs-btn--default cs-btn--small', textContent: 'Move the bits again',
        });
        retryBtn.addEventListener('click', () => {
            retryBtn.setAttribute('disabled', '');
            void (async () => {
                const again = await relockBits(
                    pending.length > 0 ? pending : args.legs, seed, { worker: bitcashWorker() });
                if (again.status === 'relocked') {
                    note.textContent = 'The bits are on the invitation now. Reprint the card so it '
                        + 'shows them.';
                    retryBtn.remove();
                    return;
                }
                pending = again.pending;
                note.textContent = again.message;
                retryBtn.removeAttribute('disabled');
            })();
        });
        content.appendChild(retryBtn);
    }

    content.appendChild(para(`Valid until ${new Date(resp.expiresAt).toLocaleDateString()}. `
        + `Roles: ${roles.join(', ')}.`
        + (fixedEmail ? ` Email fixed to ${fixedEmail}.` : '')
        + (fixedPhone ? ` Phone fixed to ${fixedPhone}.` : ''), 'cid-pane-note'));

    const doneBtn = createElement('button', { className: 'cs-btn cs-btn--yes', textContent: 'Done' });
    doneBtn.addEventListener('click', () => {
        modal.close();
        dataSource.refresh();
    });
    content.appendChild(doneBtn);
}

/** The organization's invitations: status, roles, expiry; owners can revoke pending ones. */
async function handleInvitations(shell: PaneTypeRenderContext['shell'], dataSource: DataSource<DelegateSummary>): Promise<void> {
    const orgCid = identity.getOrganizationCid();
    if (!orgCid) { shell.toast('No organization context', 'error'); return; }
    const isOwner = identity.getRoles().includes('owner');

    const content = createElement('div', { className: 'cid-wizard-step' });
    content.appendChild(createElement('h3', { textContent: 'Invitations' }));
    const listEl = createElement('div');
    content.appendChild(listEl);
    const closeBtn = createElement('button', { className: 'cs-btn cs-btn--default', textContent: 'Close' });
    content.appendChild(closeBtn);
    const modal = shell.showModal(content, { size: 'normal' });
    closeBtn.addEventListener('click', () => { modal.close(); dataSource.refresh(); });

    const refresh = async (): Promise<void> => {
        listEl.innerHTML = '';
        try {
            const { invitations } = await invitationApi.listInvitations(orgCid);
            if (invitations.length === 0) {
                listEl.appendChild(para('No invitations yet.', 'cid-pane-note'));
                return;
            }
            for (const inv of invitations) {
                const row = createElement('div', { className: 'cid-auth-checkbox-row' });
                const fixed = [inv.emailFixed ? 'email fixed' : '', inv.phoneFixed ? 'phone fixed' : ''].filter(Boolean).join(', ');
                row.appendChild(createElement('span', {
                    textContent: `#${inv.invitationId} · ${inv.roles.join(', ')} · ${inv.status}`
                        + ` · valid until ${new Date(inv.expiresAt).toLocaleDateString()}`
                        + (fixed ? ` · ${fixed}` : '')
                        + (inv.delegateCid ? ` · ${inv.delegateCid}` : ''),
                }));
                if (inv.status === 'pending' && isOwner) {
                    const revokeBtn = createElement('button', { className: 'cs-btn cs-btn--no cs-btn--small', textContent: 'Revoke' });
                    revokeBtn.addEventListener('click', async () => {
                        revokeBtn.setAttribute('disabled', '');
                        try {
                            const r = await invitationApi.revokeInvitation(orgCid, inv.invitationId);
                            shell.toast(r.voucherReleased ? 'Invitation revoked — voucher returned to the ledger' : 'Invitation revoked', 'success');
                            await refresh();
                        } catch (err) {
                            revokeBtn.removeAttribute('disabled');
                            shell.toast(formatApiError(err, 'Failed to revoke'), 'error');
                        }
                    });
                    row.appendChild(revokeBtn);

                    // ── Reclaim bits (§4.4's "Reclaim") ──────────────────────
                    // Revoking on the node frees the VOUCHER ROW. It does not
                    // move a single bit: the node has never heard of the lock,
                    // and only the VOUCHER's key can open the invitation lock —
                    // which is exactly why the buyer's condition carries no
                    // `notAfter`. This drains it into a parking lock that only
                    // the voucher key opens, where it waits for the next
                    // invitation.
                    //
                    // Offered beside Revoke rather than folded into it: the two
                    // touch different services, and an owner who revokes with
                    // the Worker down must still have revoked.
                    const reclaimBtn = createElement('button', {
                        className: 'cs-btn cs-btn--default cs-btn--small', textContent: 'Reclaim bits',
                    });
                    reclaimBtn.addEventListener('click', () => {
                        reclaimBtn.setAttribute('disabled', '');
                        void reclaimInvitationBits(shell, inv.invitationId)
                            .finally(() => reclaimBtn.removeAttribute('disabled'));
                    });
                    row.appendChild(reclaimBtn);
                }
                listEl.appendChild(row);
            }
        } catch (err) {
            listEl.appendChild(para(formatApiError(err, 'Failed to load invitations'), 'cid-pane-error cid-pane-error--visible'));
        }
    };
    void refresh();
}

export function createDelegatesListPane() {
    return createListPaneType(config);
}


/**
 * §4.4's RECLAIM: move an invitation's bits back under the buyer's own key.
 *
 * The owner is asked for the two codes because only they can supply them: the
 * INVITATION code names the lock the bits are in, and the VOUCHER code holds
 * the key that opens it. Neither exists on any server — the invitation's row is
 * a hash, and the Worker sees public keys — so a pane that tried to look them
 * up would find nothing.
 *
 * The target is a PARKING LOCK, `L(pkV, n)` with `n` probed from 2 upward
 * (slot 0 is the account lock and slot 1 the organization founder's, §4.1). Its
 * single condition is the voucher's own key with no deadline, so the money
 * waits there until the same owner points it at the next invitation.
 *
 * RACE (§10): two devices reclaiming the same voucher at once can pick the same
 * `n`; the loser's release is refused for naming an existing key, and one retry
 * lands on `n + 1`.
 */
async function reclaimInvitationBits(
    shell: PaneTypeRenderContext['shell'], invitationId: number,
): Promise<void> {
    const content = createElement('div', { className: 'cid-wizard-step' });
    content.appendChild(createElement('h3', { textContent: `Reclaim the bits of invitation #${invitationId}` }));
    content.appendChild(para('Revoking an invitation frees the voucher slot on this server, but the '
        + 'bits live on the payment service, which has never heard of this organization. Only the '
        + 'two codes can move them — and only you have those.'));
    content.appendChild(para('Enter the INVITATION code from the card you issued, and the '
        + 'REGISTRATION code you made it from. The bits are parked under your registration code, '
        + 'where they wait for the next invitation.'));

    const invInput = textInput('Invitation code (INV-…)');
    const vouInput = textInput('Registration code (RGV-…)');
    content.appendChild(invInput);
    content.appendChild(vouInput);

    const status = createElement('p', { className: 'cid-pane-note' });
    content.appendChild(status);
    const goBtn = createElement('button', { className: 'cs-btn cs-btn--yes', textContent: 'Reclaim' });
    content.appendChild(goBtn);
    const modal = shell.showModal(content, { size: 'normal' });
    const closeBtn = createElement('button', { className: 'cs-btn cs-btn--default', textContent: 'Close' });
    closeBtn.addEventListener('click', () => modal.close());
    content.appendChild(closeBtn);

    goBtn.addEventListener('click', () => {
        goBtn.setAttribute('disabled', '');
        status.textContent = 'Reading the codes…';
        void (async () => {
            try {
                const voucherCode = normalizeVoucherCode(vouInput.value);
                if (!voucherCode) throw new Error('That registration code does not look right.');
                const invitation = await deriveInvitationSecrets(invInput.value);
                const voucher = await deriveVoucherSecrets(voucherCode);

                status.textContent = 'Looking for a free parking slot…';
                const worker = bitcashWorker();
                const parking = await findFreeParkingSlot(voucher.publicKey, { worker });

                status.textContent = 'Moving the bits…';
                const target = {
                    kind: 'lock' as const,
                    key: parking.lockKey,
                    conditions: parkingConditions(voucher.publicKeyHex),
                };
                // Signed with the VOUCHER's seed: it is the second condition on
                // the invitation lock, the one with no deadline (§4.4).
                const signature = signLockRelease(
                    voucher.seed, lockKeyFor(invitation.publicKey, 0), target);
                const { amount } = await worker.releaseLock({
                    lockKey: lockKeyFor(invitation.publicKey, 0), target, signature,
                });
                status.textContent = `${unitsToBit(amount)} BIT are parked at `
                    + `${displayWalletKey(parking.lockKey) ?? parking.lockKey}. Keep your registration `
                    + 'code — it is the only thing that can move them again.';
            } catch (err) {
                goBtn.removeAttribute('disabled');
                status.textContent = err instanceof Error
                    ? err.message
                    : 'Those bits could not be reclaimed. Check both codes and try again.';
            }
        })();
    });
}
